import { DeviceConnectivity, OnOffEnum, SensorsPackage, StationTier } from '@/constants/enums';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { MetricType } from '@/constants/metric-type.enum';
import { StationPressureType } from '@/constants/stationPressureType.enum';
import { StationType } from '@/constants/stationType.enum';
import { StageTaskType, StationControlType } from '../Lifecycle/constants';
import { TaskResponse } from '../Lifecycle/types';

export const INIT_CURRENT_STATION = {
  devices: [],
  kpis: {},
  roleName: '',
};

export interface Kpi {
  currentValue?: number;
  devices: () => { powerKw: number; pressureBar: number; flowM3Min: number, deviceId: string, name: string }[];
  targetMinThreshold?: number;
  targetMaxThreshold?: number;
  shouldUseEstimatedFlow?: boolean;
}

export type AdditionalDisplayMetricSource = 'attribute' | 'data';

export type AdditionalDisplayMetric = {
  metric: string;
  source: AdditionalDisplayMetricSource;
  value: string | number | boolean | null;
};
export interface PhysicalSchema {
  toDevices: string[];
  relatedDevices: string[];
  onDevices: string[];
  elementIndex?: number;
  primaryFlowSensor?: string;
  primaryPressureSensor?: string;
  isIndependent?: boolean;
}

export interface Device {
  id: string;
  deviceId: string;
  onDevices: string[];
  connectivity: DeviceConnectivity;
  inMaintenance: boolean;
  poweredOn: OnOffEnum | string;
  parentDeviceId: string;
  name: string;
  deviceType: DeviceType;
  toDevices: string[];
  compressorNumber?: number;
  compressorName?: string;
  elementIndex?: number;
  additionalDetails?: AdditionalDisplayMetric[];
  minPressureThreshold?: number;
  maxPressureThreshold?: number;
  deviceName?: string;
  flowDegradation?: number;
  maxFlow?: number;
  pressureBar?: number;
  flowM3Min?: number;
  deviceControllerName?: string;
  modelDisplayName?: string;
  relatedDevices?: string[];
  physicalSchema?: PhysicalSchema;
  controllerId?: string;
}

export interface Draft {
  status: number;
  devices: Device[];
}

export interface Station {
  id?: string;
  customerId?: string;
  siteId?: string;
  devices?: Device[];
  stationType?: StationType;
  stationPressureType?: StationPressureType;
  stationTier?: StationTier;
  name?: string;
  sensorsPackage?: SensorsPackage[];
  kpis?: {
    pressure?: Kpi;
    flow?: Kpi;
    power?: Kpi;
  };
  draftDevices?: Draft[];
  lastActivatedUserInfo?: {
    lastEditor: string;
    activateTime: string;
  };
  unitOfMeasure?: string;
  shouldUseEstimatedFlow?: boolean;
  stationControlType?: StationControlType;
}

export interface Monitor {
  index: number;
}

export interface QueryRequestEmailTierDto {
  customMessage?: string;

  emailRequestTypeOfInterestedPackage?: string;

  emailRequestWhichScreen?: string;

  emailRequestButtonClicked?: string;
}

export interface MetricMonitor extends Monitor {
  deviceId: string;

  metric: MetricType;

  lowerThreshold?: number;

  upperThreshold?: number;
}

export interface StationKPIMonitor extends Monitor {
  metric: MetricType;

  lowerThreshold?: number;

  upperThreshold?: number;
}

export interface PressureDropMonitor extends Monitor {
  fromDeviceId: string;

  toDeviceId: string;

  lowerThreshold?: number;

  upperThreshold?: number;
}

export interface CustomMonitoredParameters {
  metricMonitors?: MetricMonitor[];

  stationKpiMonitors?: StationKPIMonitor[];

  pressureDropMonitors?: PressureDropMonitor[];
}
export interface AssetsSalesforceDTO {
  id: string;

  assetId: string;

  startDate?: Date;

  endDate?: Date;

  serialNumber18Digit?: string;

  SfsContract: string;

  accountId: string;

  accountNameStreetCity: string;

  careAgreementId?: string;

  billingCity?: string;

  billingCountry?: string;

  billingState?: string;

  cgOrgChannel?: string;

  ctsGlobalShippingAddress1?: string;

  ctsGlobalShippingCity?: string;

  ctsGlobalShippingStateProvince?: string;

  ctsSurveyDate?: Date;

  currencyIsoCode?: string;

  industry?: string;

  iritRmsCustomer?: boolean;

  helixConnectivityStatusC?: string;

  lastModifiedDate?: Date;

  accountName: string;

  rentals?: boolean;

  salesforceAccountId?: string;

  totalSiteAssetCount?: number;

  totalSiteHorsePower?: number;

  ytdServicesConnectivity?: number;

  serialNumber?: string;

  stationId?: string;

  deviceId?: string;

  epSiteId: string;
}

export interface TierPaymentPlan {
  pricePerAsset: number;
  assetCount: number;
  expectedAnnualSavingsMin: number;
  expectedAnnualSavingsMax: number;
  expectedROI: number;
  monthlyPriceBilledAnnually?: number;
  monthlyPriceBilledMonthly?: number;
}

export interface EcolightOffering {
  [StationTier.Monitor]: TierPaymentPlan;
  [StationTier.SimpleControl]: TierPaymentPlan;
  [StationTier.Optimize]: TierPaymentPlan;
}

export class Stage {
  id: number;
  name: string;
  baselineInDays?: number;
  order: number;
}

export class Status {
  id: number;
  name: string;
  baselineInDays?: number;
  order: number;
}

export class StationStageDto {
  id: number;
  stationId: string;
  startTime: string;
  endTime: Date | null;
  isCurrent: boolean;
  stage: Stage;
  userEmail?: string;
  tasks?: TaskResponse<StageTaskType>[];

  constructor(data: Partial<StationStageDto> = {}) {
    Object.assign(this, data);
  }
}
